/**
 * Hostname → app mapping.
 *
 * Each Wavlynq sibling app can be served from its own domain. When the
 * current hostname matches one of the app domains, `/` should render that
 * app's landing page instead of the Wavlynq umbrella `Index`.
 *
 * Matching is case-insensitive and ignores `www.` prefix. Add new domains
 * here as they're connected in Project Settings → Domains.
 */
export type AppKey =
  | "wavlynq"
  | "fryqnc"
  | "pulsynq"
  | "mindlynq"
  | "ablelynq"
  | "deafwav"
  | "fullynq"
  | "jadesynq"
  | "openlynq"
  | "spiritsynq";

const HOST_MAP: Record<string, AppKey> = {
  // Production custom domains
  "fryqnc.com": "fryqnc",
  "fryqnc.app": "fryqnc",
  "pulsynq.com": "pulsynq",
  "pulsynq.app": "pulsynq",
  "mindlynq.com": "mindlynq",
  "mindlynq.app": "mindlynq",
  "wavlynq.com": "wavlynq",
  "wavlynq.app": "wavlynq",
  // V2 sibling apps
  "ablelynq.com": "ablelynq",
  "ablelynq.app": "ablelynq",
  "deafwav.com": "deafwav",
  "deafwav.app": "deafwav",
  "fullynq.com": "fullynq",
  "fullynq.app": "fullynq",
  "jadesynq.com": "jadesynq",
  "jadesynq.app": "jadesynq",
  "openlynq.com": "openlynq",
  "openlynq.app": "openlynq",
  "spiritsynq.com": "spiritsynq",
  "spiritsynq.app": "spiritsynq",
};

const normalize = (host: string) =>
  host.toLowerCase().replace(/^www\./, "").split(":")[0];

/**
 * Returns the app key for the current hostname, or "wavlynq" (umbrella)
 * for preview/staging subdomains and any unmapped host.
 */
export const getAppForHost = (host?: string): AppKey => {
  const h = normalize(host ?? (typeof window !== "undefined" ? window.location.hostname : ""));

  // Direct match
  if (HOST_MAP[h]) return HOST_MAP[h];

  // Substring fallback (handles staging variants like fryqnc-staging.com,
  // app.fryqnc.com, etc.)
  if (h.includes("fryqnc")) return "fryqnc";
  if (h.includes("pulsynq")) return "pulsynq";
  if (h.includes("mindlynq")) return "mindlynq";
  if (h.includes("ablelynq")) return "ablelynq";
  if (h.includes("deafwav")) return "deafwav";
  if (h.includes("fullynq")) return "fullynq";
  if (h.includes("jadesynq")) return "jadesynq";
  if (h.includes("openlynq")) return "openlynq";
  if (h.includes("spiritsynq")) return "spiritsynq";

  return "wavlynq";
};

/** Path to the landing/home page for a given app key. */
export const homePathForApp = (app: AppKey): string => {
  switch (app) {
    case "fryqnc": return "/fryqnc";
    case "pulsynq": return "/pulsynq";
    case "mindlynq": return "/mindlynq";
    case "ablelynq": return "/ablelynq";
    case "deafwav": return "/deafwav";
    case "fullynq": return "/fullynq";
    case "jadesynq": return "/jadesynq";
    case "openlynq": return "/openlynq";
    case "spiritsynq": return "/spiritsynq";
    case "wavlynq":
    default:
      return "/";
  }
};
